// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: LGPL-2.1-only

import type { ComponentInterface } from "@stencil/core";
import { Component, h, Prop, State } from "@stencil/core";

type Width = "compact" | "standard";

@Component({
  tag: "qds-segmented",
  styleUrl: "segmented.css",
  shadow: true,
})
export class Segmented implements ComponentInterface {
  /**
   * Defines a string value used to name the segmented group through an `aria-label` attribute.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label
   * @readonly
   * @public
   * @type {string}
   */
  @Prop() public readonly label: string;

  /**
   * Segmented's width that can be compact or stretched.
   * 'Compact' width depends on the segment label width.
   * 'Standard' width stretches each segment evenly to fill the width of the control's parent. This option is intended to make the
   *
   * @remarks
   * It’s not recommended to stretch icon only segment
   *
   * @readonly
   * @public
   * @type {boolean}
   * @default false
   */
  @Prop() public readonly stretched?: boolean = false;

  @State() private isIconOnly = false;

  public render() {
    return (
      <div
        class={{
          stretch: this.isIconOnly ? false : this.stretched ?? false,
        }}
        aria-label={this.label}
        role="group"
      >
        <slot
          onSlotchange={(event: Event) => {
            this.handleIconOnly(event);
          }}
        />
      </div>
    );
  }

  private handleIconOnly(event: Event): void {
    const slot = event.target as HTMLSlotElement;
    const segmentedItems = slot.assignedElements({ flatten: true });

    this.isIconOnly = segmentedItems.some(
      (segmentedItem) => segmentedItem.getAttribute("icon-only") === "true",
    );
  }
}
